import { defineTheme } from '@astryxdesign/core/theme';
import { neutralTheme } from '@astryxdesign/theme-neutral';

/**
 * Official Astryx Neutral Theme
 */
export const astryxNeutralTheme = neutralTheme;

/**
 * Astryx Chocolate Theme (Warm Mocha / Bronze)
 */
export const astryxChocolateTheme = defineTheme({
  name: 'astryx-chocolate',
  typography: {
    scale: { base: 14, ratio: 1.2 },
    body: { family: 'Plus Jakarta Sans', fallbacks: 'system-ui, sans-serif' },
    heading: { family: 'Plus Jakarta Sans', fallbacks: 'system-ui, sans-serif', weights: { 3: 'bold', 4: 'bold' } },
  },
  tokens: {
    '--color-accent': ['#78350f', '#f59e0b'],
    '--color-accent-muted': ['#fef3c7', '#78350f33'],
    '--color-on-accent': ['#ffffff', '#1c1917'],
    '--color-background-surface': ['#ffffff', '#1c1917'],
    '--color-background-body': ['#fdfaf6', '#141210'],
    '--color-background-card': ['#fffbf5', '#1c1917'],
    '--color-text-primary': ['#292524', '#f5f5f4'],
    '--color-text-secondary': ['#78716c', '#a8a29e'],
    '--color-border': ['#e7e5e4', '#44403c'],
    '--radius-element': '0.75rem',
    '--radius-container': '1rem',
  },
});

/**
 * Modern Blue Theme (High-Tech Security Blue)
 */
export const modernBlueTheme = defineTheme({
  name: 'modern-blue',
  typography: {
    scale: { base: 14, ratio: 1.25 },
    body: { family: 'Inter', fallbacks: 'system-ui, sans-serif' },
    heading: { family: 'Hanken Grotesk', fallbacks: 'system-ui, sans-serif', weights: { 1: 'bold', 2: 'bold' } },
  },
  tokens: {
    '--color-accent': ['#0064E0', '#2694FE'],
    '--color-accent-muted': ['#EBF4FF', '#0064E033'],
    '--color-on-accent': ['#ffffff', '#ffffff'],
    '--color-background-surface': ['#ffffff', '#0f172a'],
    '--color-background-body': ['#f8fafc', '#020617'],
    '--color-background-card': ['#ffffff', '#0f172a'],
    '--color-text-primary': ['#0f172a', '#f8fafc'],
    '--color-text-secondary': ['#475569', '#94a3b8'],
    '--color-border': ['#e2e8f0', '#334155'],
    '--radius-element': '0.375rem',
    '--radius-container': '0.5rem',
  },
});

/**
 * Industrial Dark & Amber Theme (Security Guard / Hardware)
 */
export const industrialDarkTheme = defineTheme({
  name: 'industrial-dark',
  tokens: {
    '--color-accent': ['#d97706', '#fbbf24'],
    '--color-accent-muted': ['#fef3c7', '#78350f3D'],
    '--color-on-accent': ['#ffffff', '#18181b'],
    '--color-background-surface': ['#ffffff', '#18181b'],
    '--color-background-body': ['#f4f4f5', '#09090b'],
    '--color-background-card': ['#ffffff', '#18181b'],
    '--color-text-primary': ['#09090b', '#fafafa'],
    '--color-text-secondary': ['#52525b', '#a1a1aa'],
    '--color-border': ['#e4e4e7', '#27272a'],
    '--radius-element': '0.25rem',
    '--radius-container': '0.375rem',
  },
});

/**
 * Theme Presets Registry Map
 */
export const THEME_PRESETS = {
  'astryx-neutral': astryxNeutralTheme,
  'neutral': astryxNeutralTheme,
  'astryx-chocolate': astryxChocolateTheme,
  'chocolate': astryxChocolateTheme,
  'modern-blue': modernBlueTheme,
  'industrial-dark': industrialDarkTheme,
};

/**
 * Resolves an Astryx theme preset by key or returns the neutral theme fallback.
 *
 * @param {string} presetName
 * @returns {object} Astryx theme object
 */
export function getThemePreset(presetName) {
  if (!presetName) return astryxNeutralTheme;
  const normalized = String(presetName).toLowerCase().trim();
  if (Object.prototype.hasOwnProperty.call(THEME_PRESETS, normalized)) {
    return THEME_PRESETS[normalized];
  }
  return astryxNeutralTheme;
}

/**
 * Dynamically creates or customizes an Astryx theme preset based on tenant properties.
 *
 * @param {object} tenant
 * @returns {object} Astryx theme object
 */
export function createCustomTenantTheme(tenant) {
  if (!tenant) return astryxNeutralTheme;
  const presetKey = tenant.settings?.theme_preset || 'astryx-neutral';
  const baseTheme = getThemePreset(presetKey);

  // If the tenant provides custom primary / secondary colors or radius, derive a dynamic theme
  if (tenant.primaryColor || tenant.primary_color || tenant.settings?.theme_radius) {
    const primary = tenant.primaryColor || tenant.primary_color;
    const secondary = tenant.secondaryColor || tenant.secondary_color;
    const radius = tenant.settings?.theme_radius || '0.375rem';

    return defineTheme({
      name: `tenant-${tenant.slug || tenant.id || 'custom'}`,
      tokens: {
        ...(primary ? {
          '--color-accent': [primary, primary],
          '--color-accent-muted': [`${primary}22`, `${primary}33`],
        } : {}),
        ...(secondary ? {
          '--color-text-secondary': [secondary, secondary],
        } : {}),
        '--radius-element': radius,
        '--radius-container': `calc(${radius} * 1.5)`,
      },
    });
  }

  return baseTheme;
}
